<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { pushState, subscribePush, PUSH_CHANGED_EVENT, type PushState } from '../lib/push'
import { promptSnoozed, snoozePrompt } from '../lib/pushPrompt'
import { pwaVisible } from '../lib/pwa'
import {
  useBannerSpace,
  usePagePromptPresence,
  pagePromptShown,
  BANNER_SLOT_CLASS,
  BANNER_CARD_CLASS,
} from '../composables/useBannerSpace'
import UiButton from './ui/UiButton.vue'

// Предложение включить уведомления в трёх видах (поводов три: general, door,
// card_review — экран «Готово» анкеты). Рисуют они одно и то же, но
// по-разному участвуют в дележе экрана:
// banner — полоса внизу любой страницы личного кабинета; её можно закрыть, снуз
//   действует только на неё, и она же занимает место в макете (--bottom-prompt-space);
// plate — блок внутри страницы («Уведомления», «Гостевые пропуска»): человек пришёл
//   сюда сам, поэтому крестика нет и снуз не действует. Плашка стоит ближе к
//   контексту и на время своей видимости забирает право спрашивать себе — полоса
//   внизу молчит;
// popover — та же плашка, но в выпадашке колокольчика. Отличается ровно одним:
//   в дележе НЕ участвует. Выпадашка монтируется и размонтируется на каждый клик по
//   колокольчику, и считай она за занятое место — полоса внизу моргала бы вместе с ней.
//
// Тумблер в /profile при этом остаётся на месте: он единственное место, где
// уведомления можно ВЫключить и где объясняются нерабочие состояния.
const { variant, reason = 'general' } = defineProps<{
  variant: 'banner' | 'plate' | 'popover'
  reason?: 'general' | 'door' | 'card_review'
}>()

const emit = defineEmits<{ 'update:visible': [boolean] }>()

const state = ref<PushState>('unsupported')
// Состояние приезжает асинхронно (pushState() ждёт serviceWorker.ready, потолок
// 4 секунды). До ответа плашка ещё ничего не рисует, но и слот отдавать не
// должна: см. учёт ниже.
const settled = ref(false)
const snoozed = ref(false)
const busy = ref(false)
const error = ref('')

// Показываем РОВНО одно состояние — off. Всё остальное либо уже включено, либо
// объяснение, которому место у тумблера в профиле: полоса поверх страницы и
// плашка в списке для длинных объяснений не годятся.
//
// На iOS это же условие даёт бесплатный арбитраж с полосой установки: до
// добавления на домашний экран pushState() отдаёт ios_install_required, и
// предложение пуша молчит — там оно и правда бессмысленно, подписки без web app
// на iOS не существует.
// Гейт раскатки живёт здесь, а не у каждого места вставки: точек входа пять
// (полоса, колокольчик, «Оповещения», «Гостевые пропуска», экран «Готово» анкеты), и часть
// из них проверку не делала вовсе. При закрытии флага получилось бы «включить можно откуда угодно,
// а выключить негде»: тумблер в профиле прячется по тому же флагу и исчезает.
const page = usePage()

const visible = computed(() => {
  if (!pwaVisible(page.props)) return false
  if (state.value !== 'off') return false
  if (variant !== 'banner') return true

  // Полоса уступает и отложенному решению («не сейчас»), и живой плашке на странице.
  return !snoozed.value && !pagePromptShown('push')
})

const root = ref<HTMLElement | null>(null)
// Переменную отступа занимает только полоса: plate лежит внутри страницы и места
// поверх неё не отнимает. Развилка — экономия, а не защита от гонки: вклад в общую
// переменную считается по реестру всех инстансов сразу (см. useBannerSpace.ts),
// и лишний нулевой вклад ничего бы не сломал.
if (variant === 'banner') useBannerSpace(root, () => visible.value)

// Учёт по видимости, а не по монтированию: невидимая плашка ничего не занимает
// (см. usePagePromptPresence в useBannerSpace.ts). Исключение — время до ответа
// pushState(): плашка, которая ЕЩЁ не знает своего состояния, слот держит.
// Иначе при переходе между двумя страницами с плашками (/notifications →
// /profile/passes) старая снимается сразу, а новая заявляет права только после
// ответа, и полоса внизу успевает показаться — вплоть до 4 секунд, если
// serviceWorker.ready не отвечает. Полосе это ничего не стоит: она ждёт тот же
// ответ и до него всё равно невидима.
if (variant === 'plate') usePagePromptPresence('push', () => visible.value || !settled.value)

// Родителю (AppLayout) видимость нужна для арбитража нижней полосы: состояние
// приезжает асинхронно, сам он его вычислить не может. immediate — потому что
// стартовое «ничего не показываем» тоже ответ.
watch(visible, (value) => emit('update:visible', value), { immediate: true })

// pushState() может отклониться: getSubscription() внутри — браузерный API
// (registration.pushManager), а не наш код, и вправе кинуть SecurityError или
// InvalidStateError. Ловим здесь, а не даём улететь дальше: необработанный
// reject внутри void-IIFE иначе всплывает как unhandled rejection в консоли,
// а state просто останется на стартовом 'unsupported' — ровно то же самое,
// что было бы, отклонись сам браузер этой возможностью пользоваться.
// finally, а не только catch — settled ОБЯЗАН стать true на любом исходе:
// слот арбитража общий на всё SPA (visiblePagePrompts в useBannerSpace.ts,
// переменная на уровне модуля), и застрявший settled=false держал бы его за
// этим инстансом навсегда, пряча нижнюю полосу до перезагрузки страницы —
// тогда как раньше (до settled) тот же редкий сбой просто оставлял state в
// 'unsupported', и слот освобождался как обычно.
async function loadState(): Promise<void> {
  try {
    state.value = await pushState()
  } catch {
    // Оставляем state как есть (стартовое 'unsupported'): у нас нет честного
    // текста под «браузерный API внезапно отказал», а плашка/полоса при
    // 'unsupported' просто не рисуются — это безопасный отказ.
  } finally {
    settled.value = true
  }
}

void (async () => {
  snoozed.value = promptSnoozed()
  await loadState()
})()

// Уведомления включают из пяти мест сразу, а полоса внизу висит в персистентном
// макете и переспросить состояние сама не может (см. PUSH_CHANGED_EVENT в lib/push.ts).
// Без этого включивший из плашки человек продолжал бы видеть предложение в полосе,
// а повторное нажатие накручивало бы цель push_enabled.
async function refresh(): Promise<void> {
  await loadState()
}

onMounted(() => window.addEventListener(PUSH_CHANGED_EVENT, refresh))
onUnmounted(() => window.removeEventListener(PUSH_CHANGED_EVENT, refresh))

async function enable(): Promise<void> {
  busy.value = true
  error.value = ''
  try {
    // Разрешение спрашивается внутри subscribePush() и, значит, внутри этого
    // обработчика клика — вне пользовательского жеста Safari отклоняет молча.
    state.value = await subscribePush()
  } catch {
    error.value = 'Не удалось включить уведомления. Попробуйте ещё раз.'
  } finally {
    busy.value = false
  }
}

function close(): void {
  snoozePrompt()
  snoozed.value = true
}

const TEXTS = {
  door: 'Включите уведомления: если ученику не откроется дверь, вы увидите это на телефоне и откроете её прямо из уведомления.',
  card_review: 'Включите уведомления, и мы сообщим, когда проверим анкету.',
  general: 'Включите уведомления, чтобы важное по вашим броням и оплатам приходило на телефон.',
} as const

const text = computed(() => TEXTS[reason])

const wrapperClass = computed(() => (variant === 'banner' ? BANNER_SLOT_CLASS : ''))

// Рамка у карточки есть только на странице: в выпадашке колокольчика просьбу и так
// отделяет border-b её обёртки, а вторая рамка внутри узкой панели читается как
// коробка в коробке и отнимает у списка высоту.
const cardClass = computed(() => {
  if (variant === 'banner') return BANNER_CARD_CLASS
  if (variant === 'popover') return 'p-3'

  return 'rounded-lg border border-border bg-surface p-3'
})
</script>

<template>
  <div v-if="visible" ref="root" data-testid="push-prompt" class="@container" :class="wrapperClass">
    <div
      class="mx-auto flex w-full max-w-6xl flex-col gap-3 @xl:flex-row @xl:items-center @xl:gap-4"
      :class="cardClass"
    >
      <!-- На узком экране текст и кнопка стоят друг под другом: в одну строку кнопка
           отжимает объяснение в колонку шириной в пару слов (см. память
           mobile-360-squeeze-check). Раньше строка возвращалась по sm: (viewport),
           но это ломалось в popover: колокольчик открывает панель шириной ~336px
           (см. NotificationBell.vue) на любом, даже широком окне — sm: смотрит на
           окно браузера, а не на реальную ширину контейнера, и внутри узкой панели
           текст с кнопкой снова сжимались в колонку в два слова, хотя sm: формально
           был активен. @container на обёртке решает это, но первая попытка (@sm:,
           24rem/384px контейнера) сама сдвинула проблему, а не убрала: контейнер в
           384px получается уже у окна ≈416px, а не только внутри узкой панели
           колокольчика, — и на iPhone 14/15/16 Plus/Pro Max (428–430px) строка
           возвращалась бы с тем же сжатым текстом, который весь этот механизм
           должен был лечить. Порог поднят до @xl (36rem/576px контейнера): строка
           возвращается у окна ≈608px — это почти то же место, что было у старого
           sm: (640px), а колокольчик (~336px) как был колонкой, так и остаётся. -->
      <div class="min-w-0 flex-1">
        <p class="text-sm" :class="variant === 'banner' ? 'text-text' : 'text-muted'">{{ text }}</p>
        <p v-if="error" data-testid="push-prompt-error" role="alert" class="mt-2 text-sm text-crit">{{ error }}</p>
      </div>

      <div class="flex items-center justify-end gap-2 @xl:flex-none">
        <UiButton
          type="button"
          data-testid="push-prompt-button"
          :loading="busy"
          class="flex-1 whitespace-nowrap @xl:flex-none"
          @click="enable"
        >
          Включить уведомления
        </UiButton>

        <button
          v-if="variant === 'banner'"
          type="button"
          data-testid="push-prompt-close"
          aria-label="Закрыть"
          class="flex h-9 w-9 flex-none items-center justify-center rounded-md text-muted hover:bg-accent/10"
          @click="close"
        >
          ✕
        </button>
      </div>
    </div>
  </div>
</template>
